import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, Target, Sliders, RotateCcw } from 'lucide-react';
import { Metric, MetricForecast } from '../../types';

interface WhatIfScenarioProps {
  metric: Metric;
  currentForecast: MetricForecast | null;
}

interface ScenarioResult {
  dailyIncrease: number;
  projectedValue: number;
  daysToTarget: number;
  willMeetTarget: boolean;
  improvement: number;
}

export const WhatIfScenario: React.FC<WhatIfScenarioProps> = ({ metric, currentForecast }) => {
  const [scenarios, setScenarios] = useState<ScenarioResult[]>([]);
  const [customDailyIncrease, setCustomDailyIncrease] = useState('');
  const [customScenario, setCustomScenario] = useState<ScenarioResult | null>(null);

  useEffect(() => {
    generateScenarios();
  }, [metric, currentForecast]);

  const generateScenarios = () => {
    if (!metric.targetValue || !currentForecast) return;

    const currentDailyRate = currentForecast.dailyChangeRate || 0;
    const daysLeft = metric.targetDate ? 
      Math.max(1, Math.ceil((new Date(metric.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 30;
    
    const scenarios = [
      { label: '10% Increase', multiplier: 1.1 },
      { label: '25% Increase', multiplier: 1.25 },
      { label: '50% Increase', multiplier: 1.5 },
      { label: '100% Increase', multiplier: 2.0 }
    ];

    const results = scenarios.map(scenario => {
      const newDailyRate = currentDailyRate * scenario.multiplier;
      const projectedValue = metric.currentValue + (newDailyRate * daysLeft);
      const willMeetTarget = projectedValue >= metric.targetValue!;
      const improvement = ((projectedValue - (currentForecast.projectedValue || 0)) / (currentForecast.projectedValue || 1)) * 100;
      
      // Calculate days to target with new rate
      const daysToTarget = newDailyRate > 0 ? 
        Math.ceil((metric.targetValue! - metric.currentValue) / newDailyRate) : Infinity;

      return {
        dailyIncrease: (scenario.multiplier - 1) * 100,
        projectedValue: Math.round(projectedValue * 100) / 100,
        daysToTarget: Math.min(daysToTarget, 999),
        willMeetTarget,
        improvement: Math.round(improvement)
      };
    });

    setScenarios(results);
  };

  const calculateCustomScenario = () => {
    if (!customDailyIncrease || !metric.targetValue || !currentForecast) return;

    const increasePct = parseFloat(customDailyIncrease) / 100;
    const currentDailyRate = currentForecast.dailyChangeRate || 0;
    const newDailyRate = currentDailyRate * (1 + increasePct);
    
    const daysLeft = metric.targetDate ? 
      Math.max(1, Math.ceil((new Date(metric.targetDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : 30;
    
    const projectedValue = metric.currentValue + (newDailyRate * daysLeft);
    const willMeetTarget = projectedValue >= metric.targetValue;
    const improvement = ((projectedValue - (currentForecast.projectedValue || 0)) / (currentForecast.projectedValue || 1)) * 100;
    
    const daysToTarget = newDailyRate > 0 ? 
      Math.ceil((metric.targetValue - metric.currentValue) / newDailyRate) : Infinity;

    setCustomScenario({
      dailyIncrease: parseFloat(customDailyIncrease),
      projectedValue: Math.round(projectedValue * 100) / 100,
      daysToTarget: Math.min(daysToTarget, 999),
      willMeetTarget,
      improvement: Math.round(improvement)
    });
  };

  const resetCustomScenario = () => {
    setCustomDailyIncrease('');
    setCustomScenario(null);
  };

  if (!metric.targetValue || !currentForecast) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          What-If Scenarios
        </h4>
        <div className="text-center text-gray-500 py-6">
          <p>Target value required for scenario analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Calculator className="w-5 h-5" />
        What-If Scenarios
      </h4>

      <p className="text-sm text-gray-600 mb-6">
        See how different improvement rates affect your target achievement
      </p>

      {/* Current Baseline */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h5 className="font-medium text-gray-900 mb-2">Current Projection</h5>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Projected Value:</span>
            <div className="font-semibold">{currentForecast.projectedValue} {metric.unit}</div>
          </div>
          <div>
            <span className="text-gray-600">Will Meet Target:</span>
            <div className={`font-semibold ${currentForecast.willMeetTarget ? 'text-green-600' : 'text-red-600'}`}>
              {currentForecast.willMeetTarget ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
      </div>

      {/* Predefined Scenarios */}
      <div className="space-y-3 mb-6">
        <h5 className="font-medium text-gray-900">Improvement Scenarios</h5>
        {scenarios.map((scenario, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="font-medium">+{scenario.dailyIncrease}% Daily Rate</span>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                scenario.willMeetTarget 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {scenario.willMeetTarget ? 'Target Achieved' : 'Target Missed'}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Projected Value:</span>
                <div className="font-semibold">{scenario.projectedValue} {metric.unit}</div>
              </div>
              <div>
                <span className="text-gray-600">Days to Target:</span>
                <div className="font-semibold">
                  {scenario.daysToTarget === Infinity ? 'Never' : `${scenario.daysToTarget} days`}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Improvement:</span>
                <div className={`font-semibold ${scenario.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {scenario.improvement > 0 ? '+' : ''}{scenario.improvement}%
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Custom Scenario */}
      <div className="border-t border-gray-200 pt-6">
        <h5 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Sliders className="w-4 h-4" />
          Custom Scenario
        </h5>
        
        <div className="flex gap-3 mb-4">
          <div className="flex-1">
            <input
              type="number"
              min="0"
              step="5"
              value={customDailyIncrease}
              onChange={(e) => setCustomDailyIncrease(e.target.value)}
              className="input-field"
              placeholder="Enter % improvement (e.g., 20)"
            />
          </div>
          <button
            onClick={calculateCustomScenario}
            disabled={!customDailyIncrease}
            className="btn-primary px-4 py-2"
          >
            Calculate
          </button>
          <button
            onClick={resetCustomScenario}
            className="btn-secondary px-3 py-2"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {customScenario && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-900">
                  +{customScenario.dailyIncrease}% Daily Rate
                </span>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${
                customScenario.willMeetTarget 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {customScenario.willMeetTarget ? 'Target Achieved' : 'Target Missed'}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm text-blue-800">
              <div>
                <span className="text-blue-600">Projected Value:</span>
                <div className="font-semibold">{customScenario.projectedValue} {metric.unit}</div>
              </div>
              <div>
                <span className="text-blue-600">Days to Target:</span>
                <div className="font-semibold">
                  {customScenario.daysToTarget === Infinity ? 'Never' : `${customScenario.daysToTarget} days`}
                </div>
              </div>
              <div>
                <span className="text-blue-600">Improvement:</span>
                <div className={`font-semibold ${customScenario.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {customScenario.improvement > 0 ? '+' : ''}{customScenario.improvement}%
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};